// src/components/templates/auth-shell.tsx
"use client";

import type { ReactNode } from "react";
import { BrandMark } from "@/components/atoms/landing/brand-mark";
import { CookiePreferencesLink } from "@/components/atoms/landing/cookie-preferences-link";
import Grainient from "@/components/atoms/backgrounds/grainient/grainient";
import {
  AuthAside,
  type AuthAsideContent,
} from "@/components/organisms/auth/auth-aside/auth-aside";
import { defaultAuthAside } from "@/components/organisms/auth/auth-aside/auth-aside-content";

interface AuthShellProps {
  children: ReactNode;
  maxWidth?: string;
  variant?: "split" | "banner";
  /** Right-panel content (split variant only). Defaults to the brand promo. */
  aside?: AuthAsideContent;
  /** Pinned bottom-left footer in the form column (split variant only). */
  footer?: ReactNode;
}

function SplitLayout({
  children,
  maxWidth,
  aside,
  footer,
}: {
  children: ReactNode;
  maxWidth: string;
  aside: AuthAsideContent;
  footer?: ReactNode;
}) {
  return (
    <div className="grid min-h-svh lg:grid-cols-2">
      {/* Left: form column */}
      <div className="flex min-h-svh flex-col bg-white px-6 py-10 sm:px-10 lg:px-16">
        <BrandMark
          src="/niftipay-icon.svg"
          alt="Niftipay"
          width={36}
          height={36}
          className="inline-flex w-fit"
        />
        <div className="flex flex-1 flex-col justify-center py-10">
          <div className={`w-full ${maxWidth}`}>{children}</div>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 text-sm text-brand-ink/70">
          {/* One flex item, so a fragment footer (text + link) stays on one line. */}
          {footer ? <div>{footer}</div> : null}
          <CookiePreferencesLink />
        </div>
      </div>

      {/* Right: flat purple promo panel */}
      <AuthAside content={aside} />
    </div>
  );
}

function BannerLayout({
  children,
  maxWidth,
}: {
  children: ReactNode;
  maxWidth: string;
}) {
  return (
    <div className="min-h-svh bg-background">
      {/* Gradient banner */}
      <div className="relative h-28 w-full overflow-hidden">
        <div className="absolute inset-0 h-full w-full">
          <Grainient
            color1="#2563eb"
            color2="#7c3aed"
            color3="#1e40af"
            className="h-full w-full"
          />
        </div>
        <div className="relative z-10 flex h-full items-center justify-center gap-3 text-white">
          <img
            src="/images/niftipay-brandmark-white.svg"
            alt="Niftipay"
            className="h-8 w-auto"
          />
          <span className="text-xl font-bold tracking-tight">Niftipay</span>
          <span className="hidden sm:inline text-white/70">|</span>
          <span className="hidden sm:inline text-sm text-white/70">
            Accept payments seamlessly across the world.
          </span>
        </div>
      </div>

      {/* Content */}
      <div className={`mx-auto w-full ${maxWidth} px-4 py-6`}>{children}</div>

      <div className={`mx-auto w-full ${maxWidth} px-4 pb-6`}>
        <CookiePreferencesLink />
      </div>
    </div>
  );
}

export function AuthShell({
  children,
  maxWidth = "max-w-md",
  variant = "split",
  aside = defaultAuthAside,
  footer,
}: AuthShellProps) {
  if (variant === "banner") {
    return <BannerLayout maxWidth={maxWidth}>{children}</BannerLayout>;
  }

  return (
    <SplitLayout maxWidth={maxWidth} aside={aside} footer={footer}>
      {children}
    </SplitLayout>
  );
}
